<script>
import { SancareModal, SancareOcticon } from '@sancare/typescript-commons'
import { mapGetters, mapState } from 'vuex'

import { RevaluationTypeEnum } from '@/saved-searches/types.ts'
import { ModeEnum, PmsiTypeEnum } from '@/store/modules/settings/types'

import SavedSearchDetails from './SavedSearchDetails.vue'
import SavedSearchThumbnail from './SavedSearchThumbnail.vue'

export default {
  components: {
    'sancare-modal': SancareModal,
    'saved-search-thumbnail': SavedSearchThumbnail,
    'sancare-octicon': SancareOcticon,
  },
  props: {
    activeSearchIds: { type: Array, required: true },
    pmsiType: {
      validator(value) {
        return Object.values(PmsiTypeEnum).includes(value)
      },
      default: PmsiTypeEnum.MCO
    },
    mode: {
      validator(value) {
        return Object.values(ModeEnum).includes(value)
      },
      default: ModeEnum.OPTIMIZABLE,
    },
  },
  emits: ['load-saved-search'],
  data() {
    return {
      showSavedFilters: false,
      forceShowLoader: false,
    }
  },
  computed: {
    title() {
      return this.activeSearchIds.length <= 1 ? 'Recherche sélectionnée' : 'Recherches sélectionnées'
    },
    ...mapGetters(['settingsUrlQuery', 'username', 'flatSancareSettings']),
    ...mapState({
      savedSearchesRequest: (state) => state.savedSearches.fetchSavedSearchesRequest,
      earlyLimit: (state) => state.settings.earlyLimit,
      lateLimit: (state) => state.settings.lateLimit,
      hospitalizationTypes: (state) => state.settings.hospitalizationTypes,
      allSavedSearches: (state) => state.savedSearches.savedSearches,
      isSancareUser() {
        return this.$store.state.login.currentUser.sancareUser
      },
    }),
    activeSearches() {
      return this.allSavedSearches.filter((s) => this.activeSearchIds.indexOf(s.id) > -1).map((search) => ({
        ...search,
        stayListQuery: {
          mode: this.mode,
          revaluationType: RevaluationTypeEnum.UNDETERMINED,
          edit: search.id,
          searchId: undefined,
          ...this.settingsUrlQuery,
        }
      }))
    },
  },
  methods: {
    closeModal() {
      this.showSavedFilters = false
    },
    async openModal() {
      this.showSavedFilters = true
      this.forceShowLoader = true
      // load search conditions if allowed and not already done
      const searchIds = this.activeSearches.filter((s) => !s.criteriaGroups && (s.privacy !== 'sancareSearch' || this.isSancareUser || this.flatSancareSettings['hide_sancare_searches_details'] === false)).map((s) => s.id)
      if (searchIds.length > 0) {
        await this.$store.dispatch('fetchSavedSearches', {
          earlyLimit: this.earlyLimit,
          lateLimit: this.lateLimit,
          pmsiType: this.pmsiType,
          searchIds,
          hospitalizationTypes: this.hospitalizationTypes,
        })
      }
      window.setTimeout(() => this.forceShowLoader = false, 500)
    },
    loadSavedSearch(search) {
      this.closeModal()
      this.$emit('load-saved-search', search)
    },
  }
}
</script>

<template>
  <div>
    <div @click="openModal()">
      <slot name="button" />
    </div>
    <sancare-modal
      :open="showSavedFilters"
      :no-padding="true"
      @close-modal="closeModal"
    >
      <template #header>
        <div>
          <div class="row">
            <div class="sancare-modal-icon col-1">
              <sancare-octicon
                name="search"
                :width="20"
                :height="20"
              />
            </div>
            <div class="sancare-modal-title col">
              {{ title }}
            </div>
            <div
              class="sancare-modal-close col-auto"
              @click="closeModal"
            >
              <sancare-octicon
                name="x"
                :width="20"
                :height="20"
              />
            </div>
          </div>
        </div>
      </template>
      <div>
        <div v-if="savedSearchesRequest.fetching || forceShowLoader">
          <div class="loader loader-lg loader-center" />
        </div>
        <div v-if="savedSearchesRequest.error && !forceShowLoader">
          <p>{{ savedSearchesRequest.error }}</p>
        </div>
        <div
          v-if="activeSearches.length && !forceShowLoader"
          class="row p-3 g-3"
        >
          <div
            v-for="search in activeSearches"
            :key="search.id"
            class="col-3"
          >
            <saved-search-thumbnail
              :search="search"
              :select-enabled="false"
              :show-criteria-enabled="false"
              :show-settings-enabled="false"
              :toggle-system-group-enabled="false"
              :stay-list-query="search.stayListQuery"
              @consult-search="loadSavedSearch(search)"
            />
          </div>
        </div>
        <div v-if="activeSearches.length === 0 && !savedSearchesRequest.fetching && !savedSearchesRequest.error && !forceShowLoader">
          <div class="text-center">
            <div>
              <sancare-octicon
                name="telescope"
                :width="60"
                :height="60"
              />
            </div>
            <p>Aucune recherche active</p>
          </div>
        </div>
      </div>
    </sancare-modal>
  </div>
</template>
